import Alpine from 'alpinejs';
import {
    createSupabase,
    mapboxSuggest,
    mapboxRetrieve,
    newSessionToken,
    type MapboxSuggestion,
    type MapboxRetrieved,
} from '@tempesta/shared';
import formTemplate from './form.html?raw';
import './styles.scss';

interface TlsConfig {
    supabaseUrl: string;
    supabaseAnonKey: string;
    mapboxToken: string;
}

declare global {
    interface Window {
        TLS_CONFIG?: TlsConfig;
        Alpine?: typeof Alpine;
    }
}

const config: TlsConfig = window.TLS_CONFIG ?? {
    supabaseUrl: import.meta.env.VITE_SUPABASE_URL,
    supabaseAnonKey: import.meta.env.VITE_SUPABASE_ANON_KEY,
    mapboxToken: import.meta.env.VITE_MAPBOX_TOKEN,
};

const supabase = createSupabase({
    url: config.supabaseUrl,
    anonKey: config.supabaseAnonKey,
});

function scrollToSuccessAnchor() {
    window.requestAnimationFrame(() => {
        try {
            const parentDocument = window.parent === window ? document : window.parent.document;
            parentDocument.getElementById('thank-you')?.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
        } catch {}
    });
}

interface FormState {
    name: string;
    query: string;
    suggestions: MapboxSuggestion[];
    selectedPlace: MapboxRetrieved | null;
    locationAccess: 'public' | 'private';
    motivation: string;
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
    privacyAccepted: boolean;
    loading: boolean;
    error: string | null;
    success: boolean;
    sessionToken: string;
    searchTimer: number | null;
    init(): void;
    search(): void;
    select(s: MapboxSuggestion): Promise<void>;
    submit(): Promise<void>;
    reset(): void;
}

Alpine.data('tlsSubmitForm', (): FormState => ({
    name: '',
    query: '',
    suggestions: [],
    selectedPlace: null,
    locationAccess: 'public',
    motivation: '',
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    privacyAccepted: false,
    loading: false,
    error: null,
    success: false,
    sessionToken: newSessionToken(),
    searchTimer: null,

    init() {
        // ogni sessione di autocomplete dura fino al retrieve; dopo submit rigeneriamo
    },

    search() {
        if (this.searchTimer) window.clearTimeout(this.searchTimer);
        const q = this.query;

        // Se l'utente modifica il campo dopo aver selezionato, invalidiamo la scelta
        if (this.selectedPlace) this.selectedPlace = null;

        this.searchTimer = window.setTimeout(async () => {
            if (q.trim().length < 3) {
                this.suggestions = [];
                return;
            }
            try {
                this.suggestions = await mapboxSuggest(q, {
                    token: config.mapboxToken,
                    sessionToken: this.sessionToken,
                });
            } catch (e) {
                console.error('[tls] suggest error', e);
                this.suggestions = [];
            }
        }, 250);
    },

    async select(s: MapboxSuggestion) {
        try {
            const place = await mapboxRetrieve(s.mapbox_id, {
                token: config.mapboxToken,
                sessionToken: this.sessionToken,
            });
            if (!place) return;
            this.selectedPlace = place;
            this.query = place.full_address;
            this.suggestions = [];
            this.sessionToken = newSessionToken(); // prossima ricerca = nuova sessione billing
        } catch (e) {
            console.error('[tls] retrieve error', e);
            this.error = 'Impossibile caricare il dettaglio della location. Riprova.';
        }
    },

    async submit() {
        this.error = null;

        if (!this.selectedPlace) {
            this.error = 'Seleziona una location dall\'elenco dei suggerimenti.';
            return;
        }

        if (!this.privacyAccepted) {
            this.error = 'Per inviare la proposta devi accettare l\'informativa privacy e i termini del servizio.';
            return;
        }

        this.loading = true;
        const { lng, lat } = this.selectedPlace;

        const { error } = await supabase.from('locations').insert({
            type: 'user',
            name: this.name.trim(),
            // PostGIS accetta EWKT: SRID=4326;POINT(lng lat)
            location: `SRID=4326;POINT(${lng} ${lat})`,
            address: this.selectedPlace.full_address,
            address_override: this.locationAccess === 'private' ? 'Location privata' : null,
            mapbox_place_id: this.selectedPlace.mapbox_id,
            is_public: this.locationAccess === 'public',
            motivation: this.motivation.trim(),
            submitter_first_name: this.firstName.trim(),
            submitter_last_name: this.lastName.trim(),
            submitter_phone: this.phone.trim(),
            submitter_email: this.email.trim().toLowerCase(),
        });

        this.loading = false;

        if (error) {
            console.error('[tls] insert error', error);
            this.error = 'Invio non riuscito. Riprova più tardi.';
            return;
        }

        this.success = true;
        this.reset();
        scrollToSuccessAnchor();
    },

    reset() {
        this.name = '';
        this.query = '';
        this.suggestions = [];
        this.selectedPlace = null;
        this.locationAccess = 'public';
        this.motivation = '';
        this.firstName = '';
        this.lastName = '';
        this.phone = '';
        this.email = '';
        this.privacyAccepted = false;
        this.sessionToken = newSessionToken();
    },
}));

// Inietta il form in tutti i placeholder presenti nel DOM.
for (const el of document.querySelectorAll<HTMLElement>('[data-tls-submit-form]')) {
    el.innerHTML = formTemplate;
}

window.Alpine = Alpine;
Alpine.start();
